import { Fragment, useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import { useSetStorageTempMappings, useStorageTempMappings } from '@/hooks/useProducts'
import { useStorageConditions } from '@/hooks/useStorage'
import { changedMappings, groupByProfile, pendingSuggestions, profileLabel, profileOptions } from '@/lib/storageTempMapping'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import type { StorageTempMappingRow } from '@/types/products'
import type { StorageCondition } from '@/types/storage'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

/** Radix Select refuses '' as an item value, so "Not mapped" needs a token of its own. */
const NOT_MAPPED = 'NOT_MAPPED'

type Filter = 'all' | 'unmapped' | 'mapped'

/** Catalog order is the server's (unmapped first); profile groups the rows sharing a storage profile. */
type Sort = 'catalog' | 'profile'

/**
 * Product mapping tab (also Configure → Products → Storage Temp Mapping):
 * every distinct storage temperature in the product catalog and the
 * temperature it maps to, for the selected tenant.
 *
 * A product's location picker offers only units kept at its mapped
 * temperature, and the server refuses a put-away anywhere else. An unmapped
 * temperature allows any unit, which is why unmapped rows are listed first and
 * counted on the button.
 *
 * Edits are a draft until Save, which sends only the rows that differ in one
 * request. Users without `products.manage` see the same table read-only.
 */
export function ProductMappingTab({ onClose }: { onClose?: () => void }) {
  const { can } = usePermission()
  const canEdit = can(PERMISSIONS.productsManage)
  const { data, isLoading, isError, error } = useStorageTempMappings()
  const reference = useStorageConditions()
  const save = useSetStorageTempMappings()

  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<Sort>('catalog')
  /** temperatureKey → the condition chosen here; absent means "as saved". */
  const [draft, setDraft] = useState<Record<string, StorageCondition | null>>({})
  const [saved, setSaved] = useState(false)

  const rows = data?.rows ?? []
  const unmapped = data?.unmapped ?? 0
  const conditions = reference.data?.conditions ?? []

  const changes = changedMappings(rows, draft)
  // A row the user has already decided on here keeps that decision.
  const suggestions = pendingSuggestions(rows).filter(s => !(s.temperatureKey in draft))

  const needle = search.trim().toLowerCase()
  // Filtered on the SAVED condition so a row does not vanish mid-edit.
  const visible = rows.filter(
    r =>
      (filter === 'all' || (filter === 'unmapped' ? r.condition === null : r.condition !== null)) &&
      (!needle || r.storageTemperature.toLowerCase().includes(needle)),
  )
  // Grouped on the SAVED condition too, so a row does not jump group mid-edit.
  const groups =
    sort === 'profile' ? groupByProfile(visible, conditions) : [{ condition: undefined, rows: visible }]

  function effective(row: StorageTempMappingRow): StorageCondition | null {
    return row.temperatureKey in draft ? draft[row.temperatureKey] : row.condition
  }

  function choose(key: string, condition: StorageCondition | null) {
    setSaved(false)
    setDraft(prev => ({ ...prev, [key]: condition }))
  }

  function applyAllSuggestions() {
    setSaved(false)
    setDraft(prev => {
      const next = { ...prev }
      for (const s of suggestions) next[s.temperatureKey] = s.condition
      return next
    })
  }

  function handleSave() {
    save.mutate(changes, {
      onSuccess: () => {
        // The refetched rows now hold what the draft did.
        setDraft({})
        setSaved(true)
      },
    })
  }

  const chips: { value: Filter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'unmapped', label: `Unmapped (${unmapped})` },
    { value: 'mapped', label: 'Mapped' },
  ]

  return (
    <div className='flex min-h-0 flex-col gap-3'>
      <p className='text-sm text-muted-foreground'>
        Every storage temperature in the product catalog and the temperature it maps to. A product is offered
        only units kept at its mapped temperature.
      </p>

      <div className='flex flex-wrap items-center gap-2'>
        {chips.map(chip => (
          <Button
            key={chip.value}
            size='sm'
            variant={filter === chip.value ? 'default' : 'outline'}
            className='h-7 rounded-full px-3 text-xs'
            aria-pressed={filter === chip.value}
            onClick={() => setFilter(chip.value)}
          >
            {chip.label}
          </Button>
        ))}
        <Select value={sort} onValueChange={next => setSort(next as Sort)}>
          <SelectTrigger className='ml-auto h-8 w-full text-sm sm:w-48' aria-label='Sort storage temperatures'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='catalog'>Sort: Catalog order</SelectItem>
            <SelectItem value='profile'>Sort: Storage profile</SelectItem>
          </SelectContent>
        </Select>
        <div className='relative w-full sm:w-56'>
          <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder='Search temperatures'
            aria-label='Search storage temperatures'
            className='h-8 pl-8 text-sm'
          />
        </div>
      </div>

      <div className='min-h-0 flex-1 overflow-y-auto rounded-md border'>
        {isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : isError ? (
          <p className='p-4 text-sm text-destructive'>
            Could not load the mapping: {error instanceof Error ? error.message : 'unknown error'}
          </p>
        ) : (
          <Table>
            <TableHeader className='sticky top-0 z-10'>
              <TableRow>
                <TableHead className='w-[45%]'>Catalog temperature</TableHead>
                <TableHead>Maps to</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map(group => (
                <Fragment key={group.condition === undefined ? 'all' : (group.condition ?? NOT_MAPPED)}>
                  {group.condition !== undefined && (
                    <TableRow className='bg-muted/50 hover:bg-muted/50'>
                      <TableCell colSpan={2} className='py-1.5 text-xs font-medium'>
                        {group.condition === null ? 'Not mapped' : profileLabel(group.condition, conditions)}
                        <span className='ml-2 font-normal text-muted-foreground'>
                          {group.rows.length} temperature{group.rows.length === 1 ? '' : 's'}
                        </span>
                      </TableCell>
                    </TableRow>
                  )}
                  {group.rows.map(row => {
                    const value = effective(row)
                    return (
                      <TableRow
                        key={row.temperatureKey}
                        className={cn('border-l-4 border-l-transparent', value === null && 'border-l-amber-400')}
                      >
                        <TableCell className='align-top'>
                          <span className='block font-mono text-sm'>{row.storageTemperature}</span>
                          <span className='block text-xs text-muted-foreground'>
                            {/* A key mapped earlier whose codes have since gone: kept so it can be cleared. */}
                            {row.productCount === 0
                              ? 'No products'
                              : `${row.productCount.toLocaleString()} product${row.productCount === 1 ? '' : 's'}`}
                          </span>
                        </TableCell>
                        <TableCell className='align-top'>
                          <Select
                            value={value ?? NOT_MAPPED}
                            disabled={!canEdit || save.isPending}
                            onValueChange={next =>
                              choose(row.temperatureKey, next === NOT_MAPPED ? null : (next as StorageCondition))
                            }
                          >
                            <SelectTrigger
                              className='h-8 text-sm'
                              aria-label={`Temperature for ${row.storageTemperature}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NOT_MAPPED}>Not mapped</SelectItem>
                              {profileOptions(conditions, value).map(name => (
                                <SelectItem key={name} value={name}>
                                  {profileLabel(name, conditions)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {value === null && row.suggestedCondition && (
                            <p className='mt-1 text-xs text-muted-foreground'>
                              Suggested: {profileLabel(row.suggestedCondition, conditions)}
                              {canEdit && (
                                <>
                                  {' '}
                                  <button
                                    type='button'
                                    className='font-medium text-primary hover:underline'
                                    onClick={() => choose(row.temperatureKey, row.suggestedCondition)}
                                  >
                                    Apply
                                  </button>
                                </>
                              )}
                            </p>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </Fragment>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} className='py-10 text-center text-sm text-muted-foreground'>
                    {rows.length === 0
                      ? 'The catalog states no storage temperatures.'
                      : 'No storage temperature matches.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {save.error instanceof Error && <p className='text-sm text-destructive'>{save.error.message}</p>}
      {saved && (
        <p role='status' className='text-sm text-green-700'>
          Product mapping saved
        </p>
      )}

      {canEdit && (
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <Button
            variant='outline'
            disabled={suggestions.length === 0 || save.isPending}
            onClick={applyAllSuggestions}
          >
            Apply all suggestions ({suggestions.length})
          </Button>
          <div className='flex gap-2'>
            {onClose && (
              <Button variant='outline' onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button disabled={changes.length === 0 || save.isPending} onClick={handleSave}>
              {save.isPending ? 'Saving…' : `Save changes (${changes.length})`}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
